<template>
	<div class="question" :class="iosClassName">
		<v-cover
			class="question__cover"
			:image="question.bulyImage"
			:title="question.title || 'Question'"
			:description="question.description"
			overlay
		/>
		<div
			v-if="isSkinToneStep"
			class="question__skin-tones"
			:class="answersClassName"
		>
			<v-option
				v-for="answer in answers"
				:key="answer.id"
				class="question__skin-tone"
				:class="{ gradient__color: !question.bulyNoTextAnswer }"
				:image="answer.bulyImage"
				:no-text="question.bulyNoTextAnswer || question.id === 'extra'"
				:full-image="question.bulyIsimagefull"
				v-touch:tap="() => selectAnswer(answer)"
			/>
		</div>
		<div v-else class="question__answers" :class="answersClassName">
			<v-option
				v-for="answer in answers"
				:key="answer.id"
				class="question__answer"
				:class="{ gradient__color: !question.bulyNoTextAnswer }"
				:image="answer.bulyImage"
				:no-text="question.bulyNoTextAnswer || question.id === 'extra'"
				:full-image="question.bulyIsimagefull"
				v-touch:tap="() => selectAnswer(answer)"
			>
				{{ answer.title }}
			</v-option>
		</div>
	</div>
</template>

<style lang="sass">
.question_ios_fix
  height: 100svh !important
.question
  display: flex
  flex-direction: column
  height: 100vh

  &__cover
    min-height: 280px

  &__answers
    display: grid
    grid-template-columns: 50% 50%
    grid-auto-flow: row dense

  &__answer
    &:nth-child(odd):last-child
      grid-column: 1 / -1

  &__skin-tones
    position: absolute
    bottom: 0
    left:0
    right: 0
    display: grid
    grid-template-columns: auto auto auto auto auto auto auto auto auto

  &__skin-tone
    width: 100%
    height: 5rem

  @supports (-webkit-touch-callout: none)
    .question
      height: 100%
      overflow-y: scroll
      -webkit-overflow-scrolling: touch
</style>

<script>
import VCover from '@/components/VCover.vue'
import VOption from '@/components/VOption.vue'
import api from '@/api'

export default {
	name: 'Question',

	mounted() {
		if (
			!this.$route.params.id !== 'A' &&
			!this.$store.state.answers.some(id => id[0] === 'A')
		) {
			this.$router.push({ name: 'question', params: { id: 'A' } })
			return
		}

		// return next question ID
		// or 'EXTRA' instead of C
		const id = this.nextQuestionID()

		if (id) {
			this.preloadPromise = Promise.all([
				api.preloadQuestionImage(this.$t(`questions.${id}`)),
				api.preloadAnswerImages(this.$t(`answers.${id}`))
			])
		}
	},

	components: {
		VCover,
		VOption
	},

	computed: {
		question() {
			return this.$t(`questions.${this.$route.params.id}`)
		},

		isSkinToneStep() {
			return this.$route.params.id === 'extra'
		},

		answers() {
			if (this.isSkinToneStep) {
				// shitty loop to match files order
				// usability null. Should have rename files
				// fun maximum. Long time no toture arrays
				const answers = []
				const rows = 9
				const columns = 3
				for (let i = 1; i < rows + 1; i++) {
					for (let j = 0; j < columns; j++) {
						const index = rows * j + i
						answers.push({
							id: `es${index}`,
							title: '',
							bulyImage: `/img/skins/${index}.jpg`,
							analyticSlug: `skin-0${index}`,
							index
						})
					}
				}

				return answers.sort((a, b) => a.index - b.index)
			}

			return this.answersFor(this.$route.params.id)
		},

		answersClassName() {
			const className = []

			if (!this.question.bulyNoTextAnswer && this.question.bulyGradient) {
				className.push(
					`gradient gradient--${this.question.bulyGradient.replace(
						/_/g,
						'-'
					)}`
				)
			}

			return className.join(' ')
		},

		iosClassName() {
			const userAgent = window.navigator.userAgent.toLowerCase()
			const isIos = /iphone|ipad|ipod/.test(userAgent)
			const isSafari =
				/safari/.test(userAgent) &&
				!/crios/.test(userAgent) &&
				!/fxios/.test(userAgent)

			return isIos && isSafari ? 'question_ios_fix' : ''
		}
	},

	methods: {
		async selectAnswer(answer) {
			answer.bulyExternalid &&
				this.$store.commit('selectAnswer', answer.bulyExternalid)
			const id = this.nextQuestionID()

			this.$ga.send(
				this.$constants.ANALYTICS_EVENTS.PRODUCT_FINDER_STEP,
				{
					product_finder_name: this.$constants.PRODUCT_FINDER_NAME,
					product_finder_step_name: this.question.analyticSlug,
					product_finder_step_choice: answer.analyticSlug,
					product_finder_step: this.$store.state.answers.length
				}
			)
			if (id) {
				await this.preloadPromise
				this.$router.push({
					name: 'question',
					params: { id }
				})
			} else {
				this.$router.push({ name: 'result-splash' })
			}
		},

		nextQuestionID() {
			// UTF-16 OF THE CARACTER
			// SO ONLY THE FIRST ONE
			// BETWEEN 65 AND 98
			let id = this.$route.params.id.charCodeAt(0) + 1

			// ADD SKIN TONE QUESTION
			// CASE 'F' IS QUESTION BEFORE SKIN TONE
			// CASE 'extra' QUESTION AFTER THE SKIN TONE
			// switch (this.$route.params.id) {
			// case 'F':
			// 	return 'extra'
			// case 'extra':
			// 	id = 'G'.charCodeAt(0)
			// 	break
			// }

			// WE CHECK IN TRANSLATION IF THERE'S A CORRESPONDANCE
			// BECAUSE DATA ARE STORED IN TRANSLATION
			// IF THERE IS NOT, WE CHECK NEXT INDICE
			while (
				(!this.$te(`questions.${String.fromCharCode(id)}`) ||
					!this.$te(`answers.${String.fromCharCode(id)}`) ||
					!this.answersFor(String.fromCharCode(id)).length) &&
				id < 'z'.charCodeAt(0)
			) {
				id++
			}

			id = String.fromCharCode(id)

			// THEN WE RETURN ID FOUNDED
			if (this.$te(`questions.${id}`) && this.$te(`answers.${id}`)) {
				return id
			}

			return null
		},

		answersFor(id) {
			// console.log(Object.values(this.$t(`answers.${id}`)))
			return api.filter(
				this.$store.state.answers,
				Object.values(this.$t(`answers.${id}`)),
				[
					'bulyGenderCondition',
					'bulyConcern',
					'bulySkinProblemCategory1',
					'bulySkinProblemCategory2',
					'bulySkinProblemCategory3'
				]
				// ['bulyCondition', 'bulyGenderCondition']
			)
		}
	}
}
</script>
